import { useLingui } from '@lingui/react/macro';
import { useDateFormat } from '@web/services/locale';
import CarbonDateInput, { DateInputProps as CarbonDateInputProps, DateChangeEvent } from 'carbon-react/lib/components/date';
import { getSeparator } from 'carbon-react/lib/components/date/__internal__/utils/utils';
import { useState } from 'react';

export interface DateInputProps extends Omit<CarbonDateInputProps, 'onChange'> {
  onChange?: CarbonDateInputProps['onChange'];
}

export function getValidDateInputValue({ formattedValue, rawValue }: DateChangeEvent['target']['value'], dateFormat: string) {
  if (formattedValue === '' && rawValue === '') return '';

  return formattedValue.length === dateFormat.length && getSeparator(formattedValue) === getSeparator(dateFormat) ? rawValue : null;
}

export default function DateInput({ onChange, value, error, ...props }: DateInputProps) {
  const { t } = useLingui();

  const dateFormat = useDateFormat();
  const [invalidValue, setInvalidValue] = useState<string | null>(null);

  const handleOnChange = (ev: DateChangeEvent) => {
    const { formattedValue: inputFormattedDate } = ev.target.value;
    const isInvalid = getValidDateInputValue(ev.target.value, dateFormat) === null;

    setInvalidValue(isInvalid || inputFormattedDate === '' ? inputFormattedDate : null);

    onChange?.(ev);
  };

  const effectiveValue = invalidValue ?? value;
  const isDateValid = invalidValue === null;

  const errorMessage = effectiveValue !== '' && !isDateValid ? t`Invalid date` : error;

  return (
    <CarbonDateInput
      {...props}
      onChange={handleOnChange}
      value={effectiveValue}
      error={errorMessage}
      disablePortal={false}
      dateFormatOverride={dateFormat}
    />
  );
}
